import { hasTemplate } from './hasTemplate';
import { interpolate, type InterpolateOptions } from './interpolate';
import { isPlainArrayOrObject } from './isPlainArrayOrObject';

/**
 * Recursively interpolate every string leaf in a prop tree against a runtime
 * context.
 *
 * Walks arrays and plain objects, replacing any string that contains `{{…}}`
 * with the result of {@link interpolate}. Leaves without templates and all
 * non-plain values pass through unchanged.
 *
 * **Structural sharing:** subtrees with no templates keep their original
 * object / array reference. DB-1.4 renders configurable steps by passing
 * large prop trees through this function on every render; avoiding a deep
 * clone on no-op is important for downstream React memoization.
 *
 * **Cycle-safe:** object references already on the walk stack return
 * as-is (via a `WeakSet`), so malformed configs with circular structures do
 * not stack-overflow. Re-visiting the same object through two independent
 * branches is allowed (we only track the current descent path).
 *
 * **Non-plain objects** (Date, Map, Set, RegExp, class instances, etc.)
 * pass through as-is — the engine only walks arrays and plain objects. This
 * mirrors ADR-12's "pure data lookup" contract.
 *
 * **Parser errors propagate.** Any string leaf containing a malformed
 * `{{…}}` (unclosed brackets, empty expression, invalid identifier, etc.)
 * causes {@link interpolate} → {@link parse} to throw
 * {@link InterpolationError}, which aborts the entire walk. This is
 * intentional and matches ADR-12 §12 — templates are expected to be
 * validated at publish time, so a malformed template at render time is
 * a publishing bug and should surface loudly. Callers that render
 * untrusted configs should either validate up front or wrap this call
 * in a boundary.
 *
 * The generic `T` is preserved in the signature for ergonomics, but note
 * that single-expression templates in string leaves can produce non-string
 * values (see {@link interpolate}). In those cases the returned tree is a
 * structural — not a strict-type — match of the input.
 *
 * @example
 * interpolateObject(
 *   {
 *     title: 'Hello {{context.name}}',
 *     ids: '{{items[*].id}}',
 *     nested: { count: 1, label: 'x' },
 *   },
 *   { context: { name: 'Ada' }, items: [{ id: 'a' }, { id: 'b' }] },
 * );
 * // => { title: 'Hello Ada', ids: ['a', 'b'], nested: <same ref as input.nested> }
 */
export function interpolateObject<T>(
  node: T,
  context: unknown,
  options?: InterpolateOptions,
): T {
  const stack = new WeakSet<object>();
  return walk(node, context, stack, options) as T;
}

function walk(
  value: unknown,
  context: unknown,
  stack: WeakSet<object>,
  options: InterpolateOptions | undefined,
): unknown {
  if (typeof value === 'string') {
    if (!hasTemplate(value)) {
      return value;
    }
    return interpolate(value, context, options);
  }

  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (!isPlainArrayOrObject(value)) {
    return value;
  }

  if (stack.has(value)) {
    return value;
  }

  stack.add(value);
  try {
    if (Array.isArray(value)) {
      return walkArray(value, context, stack, options);
    }
    return walkObject(
      value as Record<string, unknown>,
      context,
      stack,
      options,
    );
  } finally {
    stack.delete(value);
  }
}

function walkArray(
  input: ReadonlyArray<unknown>,
  context: unknown,
  stack: WeakSet<object>,
  options: InterpolateOptions | undefined,
): ReadonlyArray<unknown> {
  let out: unknown[] | null = null;
  for (let i = 0; i < input.length; i++) {
    const original = input[i];
    const next = walk(original, context, stack, options);
    if (next !== original && out === null) {
      out = input.slice(0, i);
    }
    if (out !== null) {
      out.push(next);
    }
  }
  return out ?? input;
}

function walkObject(
  input: Record<string, unknown>,
  context: unknown,
  stack: WeakSet<object>,
  options: InterpolateOptions | undefined,
): Record<string, unknown> {
  let out: Record<string, unknown> | null = null;
  const keys = Object.keys(input);
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    const original = input[k];
    const next = walk(original, context, stack, options);
    if (next !== original && out === null) {
      out = {};
      for (let j = 0; j < i; j++) {
        out[keys[j]] = input[keys[j]];
      }
    }
    if (out !== null) {
      out[k] = next;
    }
  }
  return out ?? input;
}
